// components/ui/AccountMenu.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  ListItemIcon,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { itsRequest } from 'http/authConfig';

export interface TenantInfo {
  id?: string;
  tenantId?: string;
  name?: string;
  tenantName?: string;
  slug?: string;
  partnerName?: string;
  isCurrent?: boolean;
}

export interface Props {
  sidebarWidth?: number;
  setEmail: (email: string) => void;
  url: string | null;
  tenants: TenantInfo[];
  activeTenantId: string | null;
  onTenantChange: (tenantId: string) => void;
}

export const AccountMenu: React.FC<Props> = ({
  sidebarWidth,
  setEmail,
  url,
  tenants,
  activeTenantId,
  onTenantChange,
}) => {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const activeAccount = useMemo(() => {
    return instance.getActiveAccount() ?? accounts[0] ?? null;
  }, [instance, accounts]);

  const email =
    (activeAccount?.username as string) ||
    (activeAccount?.idTokenClaims as any)?.preferred_username ||
    '';

  // Når vi er innlogget, push e-post opp til Sidebar/AppProvider
  useEffect(() => {
    if (isAuthenticated && email) {
      setEmail(email);
    }
  }, [isAuthenticated, email, setEmail]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const handleLogin = async () => {
    handleClose();
    try {
      await instance.loginRedirect(itsRequest);
    } catch (e) {
      console.error('loginRedirect feilet', e);
    }
  };

  const handleLogout = async () => {
    handleClose();
    try {
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (e) {
      console.error('logoutRedirect feilet', e);
    }
  };

  const tenantIdFrom = (t: any) => t?.tenantId || t?.id || t?.slug || '';
  const tenantNameFrom = (t: any) => t?.tenantName || t?.name || t?.slug || '';

  const preferredTenantId =
    typeof window !== 'undefined'
      ? window.localStorage.getItem('tenantId')
      : null;
  const fallbackTenantId =
    tenants.find((t) => t.isCurrent)?.tenantId ||
    tenants.find((t) => t.isCurrent)?.id ||
    (tenants[0] ? tenantIdFrom(tenants[0]) : null);

  const effectiveTenantId =
    activeTenantId || preferredTenantId || fallbackTenantId || '';
  const effectiveTenant = tenants.find(
    (t) => tenantIdFrom(t) === effectiveTenantId
  );

  const handleTenantSelect = (e: SelectChangeEvent<string>) => {
    const newTenantId = e.target.value;
    if (!newTenantId) return;
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem('tenantId', newTenantId);
      } catch {
        /* ignore storage errors */
      }
    }
    onTenantChange(newTenantId);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 1,
        width: sidebarWidth ? sidebarWidth - 46 : 'auto',
        backgroundColor: 'rgba(255, 255, 255, 0.04)',
      }}
    >
      <IconButton onClick={handleClick} size='small' sx={{ ml: 0 }}>
        <Avatar src={url ?? undefined} sx={{ width: 36, height: 36 }} />
        <ExpandMoreIcon fontSize='small' />
      </IconButton>

      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
        <Typography variant='body2' sx={{ fontWeight: 600 }}>
          {isAuthenticated ? email || 'Innlogget' : 'Ikke innlogget'}
        </Typography>
        {effectiveTenant?.name && (
          <Typography variant='caption' color='text.secondary'>
            {effectiveTenant.name}
          </Typography>
        )}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        {/* Tenant bytte */}
        {isAuthenticated && tenants.length > 0 && (
          <Box
            sx={{
              px: 2,
              pt: 1,
              pb: 2,
              width: sidebarWidth ? sidebarWidth - 46 : 'auto',
            }}
          >
            <FormControl fullWidth size='small'>
              <InputLabel id='tenant-select-label'>Tenant</InputLabel>
              <Select
                fullWidth
                labelId='tenant-select-label'
                label='Tenant'
                value={effectiveTenantId ?? ''}
                onChange={handleTenantSelect}
              >
                {tenants.map((t) => (
                  <MenuItem
                    sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
                    key={tenantIdFrom(t)}
                    value={tenantIdFrom(t)}
                  >
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <BusinessIcon fontSize='small' />
                    </ListItemIcon>
                    <Box
                      sx={{ display: 'flex', flexDirection: 'column', mt: 1 }}
                    >
                      <Typography variant='body2'>
                        {tenantNameFrom(t)}
                      </Typography>
                      <Typography variant='caption' color='text.secondary'>
                        {t.partnerName || tenantIdFrom(t)}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {isAuthenticated && <Divider />}

        {/* Login/Logout */}
        {!isAuthenticated ? (
          <MenuItem onClick={handleLogin}>
            <ListItemIcon>
              <LoginIcon fontSize='small' />
            </ListItemIcon>
            Logg inn
          </MenuItem>
        ) : (
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize='small' />
            </ListItemIcon>
            Logg ut
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default AccountMenu;
